import React, { useEffect, useRef, useState } from 'react';
import { get, post, del } from '../utils/api';
import { SUBTYPES_INC, SUBTYPES_EXP, FOT_SUBTYPES, LOAN_SUBTYPES, hasAccrualPeriod } from '../constants/subtypes';

const MONTHS = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
const INTERNAL_COUNTERPARTIES = ['УК', 'Н44', 'Н44-офисы', 'М29', 'М19'];
const fmtDate = d => { if (!d) return d; const [y, m, day] = d.split('-'); return `${day}-${m}-${y.slice(2)}`; };

function getPeriodOptions() {
  const options = [];
  const now = new Date();
  for (let i = -2; i <= 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    options.push({ month: d.getMonth() + 1, year: d.getFullYear(), label: MONTHS[d.getMonth()] + ' ' + d.getFullYear() });
  }
  return options;
}
const PERIOD_OPTIONS = getPeriodOptions();
const periodKey = (m, y) => `${y}-${m}`;
const fmt = n => new Intl.NumberFormat('ru-RU').format(n || 0) + ' ₽';



function normalizeName(s) {
  return (s || '').toLowerCase().replace(/[^а-яёa-z\s]/gi, '').replace(/\s+/g, ' ').trim();
}

function matchContract(counterparty, contracts) {
  const cp = normalizeName(counterparty);
  if (!cp) return null;
  const cpWords = cp.split(' ').filter(w => w.length > 2);
  let best = null, bestScore = 0;
  for (const c of contracts) {
    if (c.status !== 'active') continue;
    const fullName = normalizeName(`${c.last_name} ${c.first_name} ${c.middle_name || ''}`);
    const nameWords = fullName.split(' ').filter(Boolean);
    const matches = nameWords.filter(w => cpWords.some(cw => cw === w || cw.startsWith(w) || w.startsWith(cw)));
    if (matches.length >= 2 && matches.length > bestScore) {
      bestScore = matches.length;
      best = c;
    }
  }
  return best;
}

function RowEditor({ row, contracts, utilityTypes: utilityTypesProp, properties: propertiesProp, onChange, highlight }) {
  const s = f => onChange({ ...row, ...f });
  const now = new Date();
  const [useSplit, setUseSplit] = useState(false);
  const [splitParts, setSplitParts] = useState([]);
  const [upropOpen, setUpropOpen] = useState(false);
  const [upropPos, setUpropPos] = useState({ placement: 'down', top: 0, bottom: 0, left: 0, maxHeight: 0 });
  const upropRef = useRef(null);
  const upropListRef = useRef(null);
  const [localUtilityTypes, setLocalUtilityTypes] = useState(utilityTypesProp || []);

  useEffect(() => {
    if (utilityTypesProp && utilityTypesProp.length > 0) setLocalUtilityTypes(utilityTypesProp);
    else get('/utility-types').then(d => setLocalUtilityTypes(d)).catch(() => {});
  }, [utilityTypesProp]);

  const utilityTypes = localUtilityTypes;
  const utilityProperties = (propertiesProp || []).map(p => p.number);

  const handleUpropToggle = () => {
    if (!upropOpen && upropRef.current) {
      const rect = upropRef.current.getBoundingClientRect();
      const margin = 8, gap = 4;
      const spaceBelow = window.innerHeight - rect.bottom - margin - gap;
      const spaceAbove = rect.top - margin - gap;
      if (spaceBelow >= spaceAbove) {
        setUpropPos({ placement: 'down', top: rect.bottom + gap, bottom: 0, left: rect.left, maxHeight: spaceBelow });
      } else {
        setUpropPos({ placement: 'up', top: 0, bottom: window.innerHeight - rect.top + gap, left: rect.left, maxHeight: spaceAbove });
      }
    }
    setUpropOpen(o => !o);
  };

  useEffect(() => {
    if (!upropOpen) return;
    const handler = e => {
      if (upropRef.current && !upropRef.current.contains(e.target) &&
          upropListRef.current && !upropListRef.current.contains(e.target)) setUpropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [upropOpen]);

  const isIncome = row.is_income;
  const subtypes = isIncome ? SUBTYPES_INC : SUBTYPES_EXP;

  const mkPart = (subtype, amount) => {
    const st = subtypes.find(x => x.value === subtype) || subtypes[0];
    return { subtype: st.value, action: st.type, amount: amount || '', contract_id: '', period_month: now.getMonth() + 1, period_year: now.getFullYear(), utility_property: '', utility_type: '', internal_counterparty: '' };
  };

  const toggleSplit = () => {
    if (!useSplit) {
      const parts = [mkPart(row.subtype, row.amount)];
      setSplitParts(parts);
      onChange({ ...row, splitParts: parts, periods: null });
    } else {
      setSplitParts([]);
      onChange({ ...row, splitParts: null });
    }
    setUseSplit(v => !v);
  };

  const updatePart = (i, f) => {
    const next = [...splitParts];
    const updated = { ...next[i], ...f };
    if (f.subtype) { const st = subtypes.find(x => x.value === f.subtype); if (st) updated.action = st.type; }
    next[i] = updated;
    setSplitParts(next);
    onChange({ ...row, splitParts: next });
  };

  const addPart = () => {
    const parts = [...splitParts, mkPart(subtypes[0].value, '')];
    setSplitParts(parts);
    onChange({ ...row, splitParts: parts });
  };

  const removePart = (i) => {
    const parts = splitParts.filter((_, j) => j !== i);
    setSplitParts(parts);
    onChange({ ...row, splitParts: parts });
  };

  if (row.action === 'skip') {
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <span style={{ color: 'var(--text3)', fontSize: 13 }}>Пропущено</span>
        <button className="btn btn-ghost btn-sm" onClick={() => {
          const def = isIncome ? SUBTYPES_INC[0] : SUBTYPES_EXP[0];
          s({ action: def.type, subtype: def.value });
        }}>Назначить</button>
      </div>
    );
  }

  const PeriodSelect = ({ value_month, value_year, onChange: onChg, style }) => (
    <select value={periodKey(value_month || now.getMonth() + 1, value_year || now.getFullYear())}
      onChange={e => { const opt = PERIOD_OPTIONS.find(o => periodKey(o.month, o.year) === e.target.value); if (opt) onChg(opt); }}
      style={style}>
      {PERIOD_OPTIONS.map(o => <option key={periodKey(o.month, o.year)} value={periodKey(o.month, o.year)}>{o.label}</option>)}
    </select>
  );

  const splitTotal = splitParts.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0);
  const splitRemaining = row.amount - splitTotal;
  const splitBalanced = Math.abs(splitRemaining) < 0.01;

  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>

      {/* Кнопка разбивки — всегда видна */}
      <button className={`btn btn-sm ${useSplit ? 'btn-primary' : 'btn-ghost'}`} onClick={toggleSplit} title="Разбить на части с разными назначениями">
        {useSplit ? '✂️ Разбивка вкл' : '✂️ Разбить'}
      </button>

      {/* Обычный режим (без разбивки) */}
      {!useSplit && <>
        {/* Тип / подтип */}
        <select value={row.subtype} onChange={e => {
          const st = subtypes.find(x => x.value === e.target.value);
          s({ action: st?.type || (isIncome ? 'income' : 'expense'), subtype: e.target.value });
        }} style={{ width: 200 }}>
          <option value="skip">— Пропустить —</option>
          {subtypes.map(st => <option key={st.value} value={st.value}>{st.label}</option>)}
        </select>

        {/* Договор (аренда / залог) */}
        {(row.subtype === 'rent' || row.subtype === 'deposit') && (
          <select value={row.contract_id || ''} onChange={e => s({ contract_id: e.target.value })}
            style={{ width: 210, borderColor: highlight ? 'var(--red)' : undefined, background: highlight ? 'rgba(239,68,68,.08)' : undefined }}>
            <option value="">{highlight ? '⚠ Выберите арендатора...' : 'Выберите арендатора...'}</option>
            {contracts.filter(c => c.status === 'active').map(c => (
              <option key={c.id} value={c.id}>№{c.studio_number} — {c.last_name} {c.first_name}</option>
            ))}
          </select>
        )}

        {/* Период — аренда, ФОТ или возврат арендатору */}
        {(row.subtype === 'rent' || FOT_SUBTYPES.includes(row.subtype) || row.subtype === 'tenant_refund') && (
          <PeriodSelect value_month={row.period_month} value_year={row.period_year}
            onChange={opt => s({ period_month: opt.month, period_year: opt.year })} style={{ width: 140 }} />
        )}

        {/* Возврат средств арендатору: договор */}
        {row.subtype === 'tenant_refund' && (
          <select value={row.contract_id || ''} onChange={e => s({ contract_id: e.target.value })}
            style={{ width: 210, borderColor: !row.contract_id ? 'var(--red)' : undefined }}>
            <option value="">⚠ Выберите арендатора...</option>
            {contracts.map(c => (
              <option key={c.id} value={c.id}>№{c.studio_number} — {c.last_name} {c.first_name}{c.status !== 'active' ? ' (расторгнут)' : ''}</option>
            ))}
          </select>
        )}

        {/* Займы: внутренний контрагент */}
        {LOAN_SUBTYPES.includes(row.subtype) && (
          <select value={row.internal_counterparty || ''} onChange={e => s({ internal_counterparty: e.target.value })}
            style={{ width: 160, borderColor: !row.internal_counterparty ? 'var(--red)' : undefined }}>
            <option value="">⚠ Контрагент...</option>
            {INTERNAL_COUNTERPARTIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}

        {/* Коммунальные: объект(ы) + период + вид */}
        {row.subtype === 'utility' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <div ref={upropRef} style={{ position: 'relative' }}>
                <div onClick={handleUpropToggle} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '5px 10px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, userSelect: 'none', minWidth: 140 }}>
                  <span style={{ color: (row.utility_properties || []).length === 0 ? 'var(--text3)' : 'inherit' }}>
                    {(row.utility_properties || []).length === 0 ? 'Объект...' : (row.utility_properties || []).join(', ')}
                  </span>
                  <span style={{ color: 'var(--text3)', fontSize: 9 }}>▼</span>
                </div>
                {upropOpen && (
                  <div ref={upropListRef} style={{ position: 'fixed', ...(upropPos.placement === 'down' ? { top: upropPos.top } : { bottom: upropPos.bottom }), left: upropPos.left, maxHeight: upropPos.maxHeight, overflowY: 'auto', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, zIndex: 9999, padding: '8px 12px', display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 8, rowGap: 8, alignItems: 'center', whiteSpace: 'nowrap', boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
                    {utilityProperties.map(p => (
                      <React.Fragment key={p}>
                        <input type="checkbox"
                          style={{ margin: 0, justifySelf: 'start' }}
                          id={`uprop-${row.id || ''}-${p}`}
                          checked={(row.utility_properties || []).includes(p)}
                          onChange={e => {
                            const cur = row.utility_properties || [];
                            const next = e.target.checked ? [...cur, p] : cur.filter(x => x !== p);
                            s({ utility_properties: next });
                          }}
                        />
                        <label htmlFor={`uprop-${row.id || ''}-${p}`} style={{ fontSize: 12, cursor: 'pointer', userSelect: 'none' }}>{p}</label>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
              <PeriodSelect value_month={row.period_month} value_year={row.period_year}
                onChange={opt => s({ period_month: opt.month, period_year: opt.year })} style={{ width: 140 }} />
              <select value={row.utility_type || ''} onChange={e => s({ utility_type: e.target.value })} style={{ width: 150 }}>
                <option value="">Вид услуги...</option>
                {(utilityTypes || []).filter(t => t.is_active).map(t => <option key={t.id}>{t.name}</option>)}
              </select>
            </div>
            {(row.utility_properties || []).length > 1 && (
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                {fmt(row.amount / row.utility_properties.length)} × {row.utility_properties.length} объекта
              </div>
            )}
          </div>
        )}
      </>}

      {/* Режим разбивки */}
      {useSplit && (
        <div style={{ flex: 1, minWidth: 300 }}>
          <div style={{ fontSize: 12, marginBottom: 8, fontWeight: 600, color: splitBalanced ? 'var(--green)' : 'var(--red)' }}>
            {splitBalanced
              ? `✓ Сбалансировано: ${fmt(row.amount)}`
              : `Остаток: ${fmt(splitRemaining)} из ${fmt(row.amount)}`
            }
          </div>

          {splitParts.map((part, i) => {
            const needsContract = part.subtype === 'rent' || part.subtype === 'deposit';
            const needsTenantRefund = part.subtype === 'tenant_refund';
            const needsPeriod = part.subtype === 'rent' || FOT_SUBTYPES.includes(part.subtype) || needsTenantRefund;
            const needsLoan = LOAN_SUBTYPES.includes(part.subtype);
            const needsUtility = part.subtype === 'utility';
            return (
              <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, padding: '6px 8px', background: 'var(--bg3)', borderRadius: 6, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text3)', fontSize: 12, minWidth: 18 }}>{i + 1}.</span>

                <select value={part.subtype} onChange={e => updatePart(i, { subtype: e.target.value })} style={{ width: 180 }}>
                  {subtypes.map(st => <option key={st.value} value={st.value}>{st.label}</option>)}
                </select>

                <input type="number" value={part.amount} onChange={e => updatePart(i, { amount: e.target.value })}
                  placeholder="Сумма" style={{ width: 100 }} min="0" step="0.01" />

                {needsContract && (
                  <select value={part.contract_id || ''} onChange={e => updatePart(i, { contract_id: e.target.value })} style={{ width: 180 }}>
                    <option value="">Арендатор...</option>
                    {contracts.filter(c => c.status === 'active').map(c => (
                      <option key={c.id} value={c.id}>№{c.studio_number} — {c.last_name} {c.first_name}</option>
                    ))}
                  </select>
                )}

                {needsPeriod && (
                  <PeriodSelect value_month={part.period_month} value_year={part.period_year}
                    onChange={opt => updatePart(i, { period_month: opt.month, period_year: opt.year })} style={{ width: 130 }} />
                )}

                {needsLoan && (
                  <select value={part.internal_counterparty || ''} onChange={e => updatePart(i, { internal_counterparty: e.target.value })}
                    style={{ width: 140, borderColor: !part.internal_counterparty ? 'var(--red)' : undefined }}>
                    <option value="">⚠ Контрагент...</option>
                    {INTERNAL_COUNTERPARTIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                )}

                {needsTenantRefund && (
                  <select value={part.contract_id || ''} onChange={e => updatePart(i, { contract_id: e.target.value })}
                    style={{ width: 200, borderColor: !part.contract_id ? 'var(--red)' : undefined }}>
                    <option value="">⚠ Арендатор...</option>
                    {contracts.map(c => (
                      <option key={c.id} value={c.id}>№{c.studio_number} — {c.last_name} {c.first_name}{c.status !== 'active' ? ' (расторгнут)' : ''}</option>
                    ))}
                  </select>
                )}

                {needsUtility && (<>
                  <select value={part.utility_property || ''} onChange={e => updatePart(i, { utility_property: e.target.value })} style={{ width: 120 }}>
                    <option value="">Объект...</option>
                    {utilityProperties.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <PeriodSelect value_month={part.period_month} value_year={part.period_year}
                    onChange={opt => updatePart(i, { period_month: opt.month, period_year: opt.year })} style={{ width: 130 }} />
                  <select value={part.utility_type || ''} onChange={e => updatePart(i, { utility_type: e.target.value })} style={{ width: 140 }}>
                    <option value="">Вид услуги...</option>
                    {(utilityTypes || []).filter(t => t.is_active).map(t => <option key={t.id}>{t.name}</option>)}
                  </select>
                </>)}

                {splitParts.length > 1 && (
                  <button className="btn btn-ghost btn-sm" onClick={() => removePart(i)} style={{ color: 'var(--red)', padding: '2px 6px' }}>✕</button>
                )}
              </div>
            );
          })}
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button className="btn btn-ghost btn-sm" onClick={addPart}>+ Добавить часть</button>
            {!splitBalanced && splitRemaining > 0 && splitParts.length > 0 && (
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--accent)' }}
                onClick={() => updatePart(splitParts.length - 1, { amount: String((parseFloat(splitParts[splitParts.length - 1].amount) || 0) + splitRemaining) })}>
                + добавить остаток ({fmt(splitRemaining)}) к последнему
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function TinkoffImportPage() {
  const [rows, setRows] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [utilityTypes, setUtilityTypes] = useState([]);
  const [properties, setProperties] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('api');
  const [apiFrom, setApiFrom] = useState(() => new Date().toISOString().slice(0, 7) + '-01');
  const [apiTo, setApiTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [fetching, setFetching] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    get('/utility-types').then(setUtilityTypes).catch(() => {});
    get('/properties').then(setProperties).catch(() => {});
    get('/contracts?all=1').then(c => {
      setContracts(c);
      setFetching(true);
      post('/import/tinkoff/auto-fetch', {})
        .catch(() => {})
        .finally(() => {
          get('/import/tinkoff/pending').then(d => {
            if (d.rows && d.rows.length > 0) setRows(initRows(d.rows, c));
          }).catch(() => {}).finally(() => setFetching(false));
        });
    }).catch(() => {});
  }, []);

  const initRows = (rawRows, contractsList) => {
    return rawRows.map(r => {
      const matched = r.is_income ? matchContract(r.counterparty, contractsList) : null;
      const defSubtype = r.is_income ? SUBTYPES_INC[0] : SUBTYPES_EXP[0];
      return {
        ...r,
        _key: r.operation_id || `local_${Math.random().toString(36).slice(2)}`,
        action: defSubtype.type,
        subtype: defSubtype.value,
        payment_method: 'Расчётный счёт',
        period_month: new Date().getMonth() + 1,
        period_year: new Date().getFullYear(),
        periods: null,
        splitParts: null,
        contract_id: matched ? String(matched.id) : '',
        utility_properties: [],
        utility_type: '',
        internal_counterparty: '',
        auto_matched: !!matched,
      };
    });
  };

  const fetchFromApi = async () => {
    setFetching(true); setErr('');
    try {
      const data = await post('/import/tinkoff/api-fetch', { from: apiFrom, to: apiTo });
      const newRows = initRows(data.rows, contracts);
      setRows(prev => {
        const existingIds = new Set(prev.map(r => r.operation_id).filter(Boolean));
        const merged = [...prev, ...newRows.filter(r => !existingIds.has(r.operation_id))];
        return merged;
      });
      window.dispatchEvent(new Event('pending-import-changed'));
    } catch (e) { setErr(e.message); }
    finally { setFetching(false); }
  };

  const dismissRow = async (r) => {
    if (!r.operation_id) { setRows(prev => prev.filter(x => x !== r)); return; }
    try {
      await del('/import/tinkoff/pending/' + encodeURIComponent(r.operation_id));
      setRows(prev => prev.filter(x => x.operation_id !== r.operation_id));
      window.dispatchEvent(new Event('pending-import-changed'));
    } catch (e) { setErr(e.message); }
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setErr(''); setRows([]);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const res = await fetch('/api/import/tinkoff/parse', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setRows(initRows(data.rows, contracts));
    } catch (e) { setErr(e.message); }
    finally { setUploading(false); }
  };

  const updateRow = (key, updated) => setRows(prev => prev.map(r => r._key === key ? updated : r));

  const buildTxList = r => {
    // Universal split: each part becomes its own transaction
    if (r.splitParts && r.splitParts.length > 0) {
      const desc = [r.counterparty, r.pay_purpose, r.description].filter(Boolean).join(' | ') || null;
      return r.splitParts.map(part => ({
        type: part.action,
        subtype: part.subtype,
        date: r.date,
        payment_method: 'Расчётный счёт',
        operation_id: r.operation_id || null,
        amount: parseFloat(part.amount) || 0,
        contract_id: part.contract_id || null,
        utility_type: part.utility_type || null,
        utility_property: part.utility_property || null,
        period_month: hasAccrualPeriod(part.subtype) ? part.period_month || null : null,
        period_year: hasAccrualPeriod(part.subtype) ? part.period_year || null : null,
        counterparty: part.internal_counterparty || null,
        description: desc,
        periods: null,
      }));
    }

    const base = {
      type: r.action,
      subtype: r.subtype,
      date: r.date,
      payment_method: 'Расчётный счёт',
      operation_id: r.operation_id || null,
      contract_id: r.contract_id || null,
      utility_type: r.utility_type || null,
      period_month: hasAccrualPeriod(r.subtype) ? r.period_month || null : null,
      period_year: hasAccrualPeriod(r.subtype) ? r.period_year || null : null,
      description: [r.counterparty, r.pay_purpose, r.description].filter(Boolean).join(' | ') || null,
      counterparty: r.internal_counterparty || null,
      periods: r.periods || null,
    };
    const props = r.utility_properties || [];
    if (r.subtype === 'utility' && props.length > 1) {
      // Деление с округлением до копеек; остаток — на последний объект
      const total = Math.round(r.amount * 100);
      const n = props.length;
      const baseCents = Math.floor(total / n);
      return props.map((prop, i) => ({
        ...base,
        amount: (i === n - 1 ? total - baseCents * (n - 1) : baseCents) / 100,
        utility_property: prop,
      }));
    }
    return [{ ...base, amount: r.amount, utility_property: props[0] || null }];
  };

  const splitBalanceOk = (r) => {
    if (r.splitParts && r.splitParts.length > 0) {
      const total = r.splitParts.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
      return Math.abs(total - r.amount) < 0.01;
    }
    if (!r.periods || r.periods.length === 0) return true;
    const total = r.periods.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
    return Math.abs(total - r.amount) < 0.01;
  };

  // Коммуналка + ФОТ (зарплаты) — период начисления обязателен
  const PERIOD_REQUIRED = new Set(['utility', ...FOT_SUBTYPES]);
  const periodOk = (r) => buildTxList(r).every(t => {
    if (!PERIOD_REQUIRED.has(t.subtype)) return true;
    if (t.periods && t.periods.length > 0) return t.periods.every(p => p.month && p.year);
    return !!(t.period_month && t.period_year);
  });

  const confirmOne = async (key) => {
    const r = rows.find(r => r._key === key);
    if (!r || r.action === 'skip') return;
    if (!splitBalanceOk(r)) return setErr(`Разбивка транзакции от ${r.date} не сбалансирована: сумма частей не равна ${fmt(r.amount)}`);
    if (!periodOk(r)) return setErr(`Укажите период начисления для коммунальных/зарплатных строк от ${r.date}`);
    try {
      const operation_ids = r.operation_id ? [r.operation_id] : [];
      await post('/import/tinkoff/confirm', { transactions: buildTxList(r), operation_ids });
      setRows(prev => prev.filter(r => r._key !== key));
      window.dispatchEvent(new Event('pending-import-changed'));
    } catch (e) { setErr(e.message); }
  };

  const confirm = async () => {
    const toSave = rows.filter(r => r.action !== 'skip');
    if (toSave.length === 0) return setErr('Нет транзакций для сохранения');
    const badSplit = toSave.find(r => !splitBalanceOk(r));
    if (badSplit) return setErr(`Разбивка транзакции от ${badSplit.date} на ${fmt(badSplit.amount)} не сбалансирована. Исправьте перед сохранением.`);
    const badPeriod = toSave.find(r => !periodOk(r));
    if (badPeriod) return setErr(`Не указан период начисления для коммунальных/зарплатных строк от ${badPeriod.date}. Исправьте перед сохранением.`);
    setSaving(true); setErr('');
    try {
      const operation_ids = rows.filter(r => r.action !== 'skip' && r.operation_id).map(r => r.operation_id);
      const result = await post('/import/tinkoff/confirm', { transactions: toSave.flatMap(buildTxList), operation_ids });
      setSaved(result.saved);
      setRows([]);
      window.dispatchEvent(new Event('pending-import-changed'));
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const skipCount = rows.filter(r => r.action === 'skip').length;
  const activeCount = rows.length - skipCount;
  const unmatchedCount = rows.filter(r => {
    if (r.action === 'skip') return false;
    if (r.splitParts && r.splitParts.length > 0) {
      return r.splitParts.some(p => (p.subtype === 'rent' || p.subtype === 'deposit' || p.subtype === 'tenant_refund') && !p.contract_id);
    }
    return ((r.is_income && (r.subtype === 'rent' || r.subtype === 'deposit')) || r.subtype === 'tenant_refund') && !r.contract_id;
  }).length;

  const [sortCol, setSortCol] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const toggleSort = col => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const SortTh = ({ col, label, style }) => (
    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', padding: '10px 14px', textAlign: 'left', ...style }}
      onClick={() => toggleSort(col)}>
      {label} <span style={{ opacity: sortCol === col ? 1 : 0.25, fontSize: 10 }}>{sortCol === col ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</span>
    </th>
  );
  const sortedRows = [...rows].sort((a, b) => {
    let va, vb;
    switch (sortCol) {
      case 'date':        va = a.date; vb = b.date; break;
      case 'amount':      va = (a.is_income ? 1 : -1) * a.amount; vb = (b.is_income ? 1 : -1) * b.amount; break;
      case 'counterparty': va = (a.counterparty || a.description || '').toLowerCase(); vb = (b.counterparty || b.description || '').toLowerCase(); break;
      default: return 0;
    }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Импорт Т-Банк</h1>
      </div>

      {saved !== null ? (
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>✅</div>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Импорт завершён</div>
          <div style={{ color: 'var(--text2)' }}>Сохранено транзакций: <strong>{saved}</strong></div>
          <button className="btn btn-primary" style={{ marginTop: 20 }} onClick={() => setSaved(null)}>Загрузить ещё</button>
        </div>
      ) : (
        <>
          {rows.length === 0 && (
            <>
              {fetching ? (
                <div className="card" style={{ textAlign: 'center', padding: 48 }}>
                  <div style={{ fontSize: 32, marginBottom: 16 }}>⏳</div>
                  <div style={{ fontSize: 16, color: 'var(--text2)' }}>Загружаю операции из Т-Банк...</div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                    <button className={`btn ${tab === 'csv' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('csv')}>📂 CSV-файл</button>
                  </div>
                  {err && <div className="error-msg" style={{ marginBottom: 16 }}>{err}</div>}
                  <div className="card" style={{ textAlign: 'center', padding: 48 }}>
                    <div style={{ fontSize: 32, marginBottom: 16 }}>✅</div>
                    <div style={{ fontSize: 16, color: 'var(--text2)', marginBottom: 20 }}>Новых операций нет</div>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <button className="btn btn-ghost" onClick={() => { setFetching(true); post('/import/tinkoff/auto-fetch', {}).catch(() => {}).finally(() => { get('/import/tinkoff/pending').then(d => { if (d.rows && d.rows.length > 0) setRows(initRows(d.rows, contracts)); }).catch(() => {}).finally(() => setFetching(false)); }); }}>
                        🔄 Обновить
                      </button>
                      <button className={`btn ${tab === 'csv' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('csv')}>📂 Загрузить CSV</button>
                    </div>
                    {tab === 'csv' && (
                      <div style={{ marginTop: 24 }}>
                        <input ref={fileRef} type="file" accept=".csv,.txt" style={{ display: 'none' }} onChange={handleFile} />
                        <button className="btn btn-primary" onClick={() => fileRef.current.click()} disabled={uploading}>
                          {uploading ? 'Разбор файла...' : 'Выбрать файл'}
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {rows.length > 0 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
                <div style={{ fontSize: 14, color: 'var(--text2)', display: 'flex', gap: 16 }}>
                  <span>Операций: <strong>{rows.length}</strong></span>
                  <span style={{ color: 'var(--green)' }}>Сохранить: <strong>{activeCount}</strong></span>
                  <span style={{ color: 'var(--text3)' }}>Пропустить: <strong>{skipCount}</strong></span>
                  {unmatchedCount > 0 && <span style={{ color: 'var(--red)' }}>⚠ Без арендатора: <strong>{unmatchedCount}</strong></span>}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {err && <span style={{ color: 'var(--red)', fontSize: 13 }}>{err}</span>}
                  <button className="btn btn-ghost" onClick={() => setRows([])} title="Скрыть список (операции сохранятся как непроведённые)">Скрыть</button>
                  <button className="btn btn-primary" onClick={confirm} disabled={saving || activeCount === 0 || unmatchedCount > 0}
                    title={unmatchedCount > 0 ? 'Назначьте арендатора для всех выделенных строк' : ''}>
                    {saving ? 'Сохранение...' : `Сохранить ${activeCount} операций`}
                  </button>
                </div>
              </div>

              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg3)' }}>
                      <SortTh col="date" label="Дата" style={{ width: 90 }} />
                      <SortTh col="amount" label="Сумма" style={{ width: 110 }} />
                      <SortTh col="counterparty" label="Контрагент / назначение" />
                      <th style={{ padding: '10px 14px', textAlign: 'left', minWidth: 440 }}>Назначение в системе</th>
                      <th style={{ padding: '10px 14px', width: 110 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((r) => {
                      const needsContract = r.action !== 'skip' &&
                        ((r.is_income && (r.subtype === 'rent' || r.subtype === 'deposit')) || r.subtype === 'tenant_refund') &&
                        !r.contract_id &&
                        !(r.splitParts && r.splitParts.length > 0);
                      return (
                        <tr key={r._key} style={{
                          borderTop: '1px solid var(--border)',
                          opacity: r.action === 'skip' ? 0.45 : 1,
                          background: needsContract ? 'rgba(239,68,68,.05)' : undefined
                        }}>
                          <td style={{ padding: '10px 14px', color: 'var(--text3)', whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</td>
                          <td style={{ padding: '10px 14px', fontWeight: 700, whiteSpace: 'nowrap',
                            color: r.is_income ? 'var(--green)' : 'var(--red)' }}>
                            {r.is_income ? '+' : '-'}{fmt(r.amount)}
                          </td>
                          <td style={{ padding: '10px 14px', maxWidth: 260 }}>
                            {r.counterparty && <div style={{ fontWeight: 600, marginBottom: 2 }}>{r.counterparty}</div>}
                            {r.pay_purpose && <div style={{ fontSize: 12, color: 'var(--text2)', wordBreak: 'break-word' }}>{r.pay_purpose}</div>}
                            {!r.counterparty && !r.pay_purpose && r.description && <div style={{ color: 'var(--text2)' }}>{r.description}</div>}
                            {r.auto_matched && <div style={{ fontSize: 11, color: 'var(--green)', marginTop: 2 }}>✓ авто</div>}
                            {needsContract && <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 2 }}>⚠ нужно выбрать</div>}
                          </td>
                          <td style={{ padding: '10px 14px' }}>
                            <RowEditor
                              row={r}
                              contracts={contracts}
                              utilityTypes={utilityTypes}
                              properties={properties}
                              onChange={updated => updateRow(r._key, updated)}
                              highlight={needsContract}
                            />
                          </td>
                          <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                              {r.action !== 'skip' && (
                                <button className="btn btn-primary btn-sm" onClick={() => confirmOne(r._key)}
                                  disabled={needsContract} title={needsContract ? 'Выберите арендатора' : ''}>
                                  Провести
                                </button>
                              )}
                              <button className="btn btn-ghost btn-sm" onClick={() => dismissRow(r)}
                                title="Удалить (не проводить, не импортировать повторно)"
                                style={{ color: 'var(--red)', opacity: 0.7 }}>
                                🗑
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
